import * as layout from '../../../components/markLayout.module.css';
import Section from '@hero/ui-kit/layout/Section';
import MarkModal from '../../../components/MarkModal';
import { MarkButton, MarkHeading } from '../../../components/MarkControls';
import React, { useId } from 'react';
import P from '@hero/ui-kit/typography/P';
import useResumeBlast from '../../../api/useResumeBlast';
import { useNotifications } from '../../../../../context/notification';
import { extractBlastErrorMessage } from '../blastFormUtils';
import type { BlastDetail } from '../../../types';
type Props = {
    blast: BlastDetail;
    onClose: () => void;
    onSuccess: () => void;
};
const ResumeModal: React.FC<Props> = ({ blast, onClose, onSuccess }) => {
    const resume = useResumeBlast();
    const { addNotification } = useNotifications();
    const headingId = useId();
    const handleConfirm = () => {
        resume.mutate(
            { blast_id: blast.blast_id },
            {
                onSuccess: (response) => {
                    const count = response.data.pending_count;
                    addNotification({
                        type: 'success',
                        message: `Blast resumed — ${count.toLocaleString()} recipients queued.`
                    });
                    onSuccess();
                }
            }
        );
    };
    return (
        <MarkModal
            onClose={onClose}
            isPending={resume.isPending}
            aria-labelledby={headingId}
            styles={{
                width: 'min(64rem, 90vw)'
            }}
        >
            <Section noDefaultPadding className={layout.form}>
                <MarkHeading
                    role="h4"
                    id={headingId}
                    tabIndex={-1}
                    style={{
                        margin: '0 0 1rem'
                    }}
                >
                    Resume blast
                </MarkHeading>
                <P
                    noDefaultMargin
                    styles={{
                        marginBottom: '0.8rem'
                    }}
                >
                    Resume re-dispatches unsent recipients from the frozen snapshot.
                </P>
                <P
                    noDefaultMargin
                    size="small"
                    styles={{
                        color: 'var(--mark-warning)',
                        backgroundColor: 'var(--mark-warning-surface)',
                        padding: '0.4rem 0.8rem',
                        borderRadius: '0.4rem',
                        marginBottom: '0.8rem'
                    }}
                >
                    The segment is NOT re-evaluated — anyone added since the snapshot will not be
                    sent to.
                </P>
                {resume.error && (
                    <div
                        role="alert"
                        style={{
                            marginTop: '0.8rem'
                        }}
                        className={layout.notice}
                    >
                        <P
                            noDefaultMargin
                            size="small"
                            styles={{
                                color: 'var(--mark-danger)'
                            }}
                        >
                            {extractBlastErrorMessage(resume.error)}
                        </P>
                    </div>
                )}
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: '0.8rem',
                        marginTop: '1.6rem'
                    }}
                >
                    <MarkButton type="button" onClick={onClose} disabled={resume.isPending}>
                        Cancel
                    </MarkButton>
                    <MarkButton
                        type="button"
                        onClick={handleConfirm}
                        disabled={resume.isPending}
                        variant="primary"
                    >
                        {resume.isPending ? 'Resuming…' : 'Resume'}
                    </MarkButton>
                </div>
            </Section>
        </MarkModal>
    );
};
export default ResumeModal;
